import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {API_BASE_URL} from "../../api/Settings";

// ---- Types ---------------------------------------------------------------
export type TaskPriority = "LOW" | "MEDIUM" | "HIGH";

export interface Task {
    idt: number; // assuming your entities use `idt` as PK
    num: number;
    title: string;
    description?: string;
    /** ISO string in UTC */
    dueDate?: string;
    priority: TaskPriority;
    // You can extend with: createdAt, updatedAt, status, etc.
}

export interface NewTaskPayload {
    title: string;
    description?: string;
    /** ISO string in UTC */
    dueDate?: string;
    priority: TaskPriority;
}

interface TasksState {
    items: Task[];
    creating: "idle" | "loading" | "succeeded" | "failed";
    loadingList?: "idle" | "loading" | "succeeded" | "failed"; // 👈
    error?: string;
    listError?: string; // 👈
}

const initialState: TasksState = {
    items: [],
    creating: "idle",
    loadingList: "idle",
};

// ---- Thunks --------------------------------------------------------------
/**
 * POST /tasks to create a new task and return it.
 * Reads the JWT token from state.user.token.
 */
export const createTask = createAsyncThunk<Task, NewTaskPayload, { state: any }>(
    'tasks/createTask',
    async (payload, {getState}) => {
        const state: any = getState();
        const token = state?.user?.token;
        const firmId = state?.user?.firm?.idt;
        if (!token) throw new Error('Not authenticated');
        if (!firmId) throw new Error('Aucune firm sélectionnée');

        const res = await fetch(`${API_BASE_URL}/firms/${firmId}/tasks`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Échec de la création de la tâche');
        return data as Task;
    }
);

// ---- Slice ---------------------------------------------------------------
const tasksSlice = createSlice({
    name: "tasks",
    initialState,
    reducers: {
        /** Optionnel: insérer une tâche localement (ex. import ou seed) */
        insertTaskLocal: (state, action: PayloadAction<Task>) => {
            const exists = state.items.some((t) => t.idt === action.payload.idt);
            if (!exists) state.items.unshift(action.payload);
        },
        /** Optionnel: reset des erreurs/état */
        resetCreateState: (state) => {
            state.creating = "idle";
            state.error = undefined;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createTask.pending, (state) => {
                state.creating = "loading";
                state.error = undefined;
            })
            .addCase(createTask.fulfilled, (state, action) => {
                state.creating = "succeeded";
                state.items.unshift(action.payload);
            })
            .addCase(createTask.rejected, (state, action) => {
                state.creating = "failed";
                state.error = (action.error?.message as string) || "Erreur inconnue";
            })
            .addCase(fetchTasksByFirm.pending, (state) => {
                state.loadingList = "loading";
                state.listError = undefined;
            })
            .addCase(fetchTasksByFirm.fulfilled, (state, action) => {
                state.loadingList = "succeeded";
                state.items = action.payload; // on remplace par les données serveur
            })
            .addCase(fetchTasksByFirm.rejected, (state, action) => {
                state.loadingList = 'failed';
                const message = action.error?.message;
                if (message?.includes('498')) {
                    state.listError = 'Token invalide – redirection vers login';
                } else {
                    state.listError = message || 'Erreur inconnue';
                }
            });
    },
});

export const fetchTasksByFirm = createAsyncThunk<
    Task[],          // retour: liste de tâches
    void,            // pas d’argument: on lit firmId depuis le state
    { state: any }
>(
    "tasks/fetchByFirm",
    async (_, thunkAPI) => {
        const state: any = thunkAPI.getState();
        const token: string | null | undefined = state?.user?.token;
        const firmId: number | undefined = state?.user?.firm?.idt;

        console.log('Fetching tasks for firmId:', firmId);

        if (!token) throw new Error("Not authenticated");
        if (!firmId) throw new Error("Aucune firme sélectionnée");

        const res = await fetch(`${API_BASE_URL}/firms/${firmId}/tasks`, {
            headers: {
                method: 'GET',
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        console.log('res', res);
        if (res.status === 498) {
            throw new Error("498: Token invalide");
        } else {
            const data = await res.json().catch(() => ({}));
            console.log('Fetched tasks data:', data);
            if (!res.ok)
                throw new Error(data?.message || "Échec du chargement des tâches");
            return data as Task[];
        }
    }
);

export const {insertTaskLocal, resetCreateState} = tasksSlice.actions;
export default tasksSlice.reducer;

// ---- Selectors -----------------------------------------------------------
export const selectTasks = (state: any): Task[] => state.tasks?.items ?? [];
export const selectTasksCreating = (state: any): TasksState["creating"] => state.tasks?.creating ?? "idle";
export const selectTasksLoading = (state: any) => state.tasks?.loadingList === "loading";
export const selectTasksError = (state: any) => state.tasks?.listError;
// export const selectTasksError = (state: any): string | undefined => state.tasks?.error;
